import { inject, Injectable } from '@angular/core'
import { FeathersService } from '../feathers.service'
import { defer, from, Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { SocialServiceHandleEnum } from '../../shared/enums/social-service-handles.enum'

export interface ActiveAllocationManagerPayload {
  socialServiceId: number
  locationId: number
}

export interface ActiveAllocationCurrentUser {
  allocationId: number
  socialServiceId: number
  locationId: number
  handle?: SocialServiceHandleEnum
}

@Injectable({
  providedIn: 'root',
})
export class ActiveAllocationManagerService {
  private feathersService = inject(FeathersService)

  private readonly service = 'features/active-allocation-manager'

  set(socialServiceId: number, locationId: number): Observable<any> {
    const payload: ActiveAllocationManagerPayload = { socialServiceId, locationId }
    const service = this.feathersService.service(this.service)
    return from(service.create(payload)).pipe(
      map((response: any) => response),
      catchError((error) => {
        console.error('Error in ActiveAllocationManagerService:', error)
        throw error
      }),
    )
  }

  get(): Observable<ActiveAllocationCurrentUser[]> {
    const service = this.feathersService.service(this.service)
    return from(service.find()).pipe(
      map((response: any) => {
        console.log('Service response:', response)
        return response.data || response
      }),
      catchError((error) => {
        console.error('Error in ActiveAllocationManagerService (get):', error)
        throw error
      }),
    )
  }

  /**
   * Resolves the current user's active allocation id, or `null` if it can't be determined
   * (e.g. service unavailable, no active allocation). Never throws — safe to call from
   * component init for optional features like tag filters. `defer` captures any synchronous
   * throw from `get()` into the error channel so `catchError` can handle it.
   */
  getCurrentAllocationId(): Observable<number | null> {
    return defer(() => this.get()).pipe(
      map((allocations) => allocations?.[0]?.allocationId ?? null),
      catchError(() => of(null)),
    )
  }
}
